import React, { useRef, useState, useEffect, useMemo } from 'react';
import './KabloBacasiPanel.css';
import { usePanels } from '../../../context/PanelsContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectMetadata, updateSimpleSupplyMetadata } from '../../../redux/simpleSupplySlice';
import { incrementUpdateCounter } from '../../../redux/utilsSlice';
import { selectSortedFloorPlans, FLOOR_TYPES } from '../../../redux/floorPlanSlice';
import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';

const PANEL_NAME = 'KabloBacasiPanel';

const FIELDS = [
  { key: 'K',   label: 'K (Kat Sayisi)',              type: 'int',   step: 1,    min: 1,    max: 50,  auto: true },
  { key: 'A',   label: 'A (Kat Basi Daire)',          type: 'int',   step: 1,    min: 1,    max: 20,  auto: true },
  { key: 'Rt',  label: 'Rt (Tesisat Kablo, cm)',      type: 'float', step: 0.1,  min: 0.1,  max: 5,   default: 0.5 },
  { key: 'Rtv', label: 'Rtv (TV/Tel Kablo, cm)',      type: 'float', step: 0.1,  min: 0.1,  max: 5,   default: 0.6 },
  { key: 'Rk',  label: 'Rk (Kolon Hatti, cm)',        type: 'float', step: 0.1,  min: 0.1,  max: 10,  default: 1.8 },
  { key: 'TIP', label: 'TIP (Kablo Merdiveni)',       type: 'int',   step: 1,    min: 1,    max: 4,   default: 4 },
  { key: 'd',   label: 'd (Dikme Profil, cm)',        type: 'float', step: 0.01, min: 0.05, max: 1,   default: 0.15 },
  { key: 'L',   label: 'L (Mesnet Acikligi, cm)',     type: 'int',   step: 10,   min: 50,   max: 500, default: 150 },
];

const KabloBacasiPanel = () => {
  const dispatch = useDispatch();
  const metadata = useSelector(selectMetadata);
  const simpleSupply = useSelector((state) => state.simpleSupply);
  const floorPlans = useSelector(selectSortedFloorPlans);
  const cableShaft = metadata?.cableShaft || {};

  // ── Otomatik hesaplanan K ve A ──
  const derivedK = useMemo(() => {
    if (!Array.isArray(floorPlans) || floorPlans.length === 0) return 5;
    return floorPlans.reduce((sum, p) => sum + (p.floorCount || 1), 0);
  }, [floorPlans]);

  const derivedA = useMemo(() => {
    const comps = simpleSupply?.components;
    if (!comps || !Array.isArray(comps)) return 1;
    const panelCount = comps.filter(c =>
      c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL
    ).length;
    if (panelCount === 0) return 1;
    const normalFloors = Array.isArray(floorPlans)
      ? floorPlans.filter(p => p.type === FLOOR_TYPES.NORMAL)
          .reduce((s, p) => s + (p.floorCount || 1), 0) || 1
      : 1;
    return Math.round(panelCount / normalFloors) || 1;
  }, [simpleSupply, floorPlans]);

  // Otomatik değerler değişince metadata'da override yoksa güncelle
  useEffect(() => {
    const updates = {};
    let needsUpdate = false;

    if (!cableShaft._overrideK && cableShaft.K !== derivedK) {
      updates.K = derivedK;
      needsUpdate = true;
    }
    if (!cableShaft._overrideA && cableShaft.A !== derivedA) {
      updates.A = derivedA;
      needsUpdate = true;
    }

    if (needsUpdate) {
      dispatch(updateSimpleSupplyMetadata({
        cableShaft: { ...cableShaft, ...updates }
      }));
      // incrementUpdateCounter burada çağrılmaz - sadece metadata güncellenir.
      // Doküman yeniden çizimi "Proje Oluştur" veya "Dokümanları Güncelle" butonuyla tetiklenir.
    }
  }, [derivedK, derivedA]);

  const getValue = (field) => {
    if (field.auto) {
      const override = cableShaft[`_override${field.key}`];
      if (override) return cableShaft[field.key] ?? (field.key === 'K' ? derivedK : derivedA);
      return field.key === 'K' ? derivedK : derivedA;
    }
    return cableShaft[field.key] ?? field.default;
  };

  const isAutoField = (field) => {
    if (!field.auto) return false;
    return !cableShaft[`_override${field.key}`];
  };

  const handleChange = (key, value) => {
    const field = FIELDS.find(f => f.key === key);
    const num = field?.type === 'int' ? parseInt(value, 10) : parseFloat(value);
    if (isNaN(num)) return;

    const update = { ...cableShaft, [key]: num };
    // K veya A değiştirilirse override flag'ini set et
    if (field?.auto) {
      update[`_override${key}`] = true;
    }

    dispatch(updateSimpleSupplyMetadata({ cableShaft: update }));
    dispatch(incrementUpdateCounter());
  };

  const handleReset = (key) => {
    const update = { ...cableShaft };
    delete update[`_override${key}`];
    // Otomatik değeri geri yaz
    if (key === 'K') update.K = derivedK;
    if (key === 'A') update.A = derivedA;

    dispatch(updateSimpleSupplyMetadata({ cableShaft: update }));
    dispatch(incrementUpdateCounter());
  };

  // ─── Panel sürükleme / boyutlandırma ───
  const ref = useRef(null);
  const headerRef = useRef(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ width: 0, height: 0 });

  const [collapsed, setCollapsed] = useState(true);
  const [panelHeight] = useState(400);

  const TOPBAR_HEIGHT = 115;
  const SNAP_THRESHOLD = 30;

  const { registerPanel, updatePanelPosition, panels, setPanelHeight, getStackTop, activeTabId } = usePanels();
  const wasDragged = useRef(false);

  const toggle = () => setCollapsed(prev => !prev);

  const showSnapHighlight = () => {
    const el = ref.current;
    if (!el) return;
    el.classList.add('snap-highlight');
    setTimeout(() => el.classList.remove('snap-highlight'), 300);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    registerPanel(PANEL_NAME, {
      top: rect.top, left: rect.left,
      width: rect.width, height: rect.height,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      updatePanelPosition(PANEL_NAME, {
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height, collapsed,
      });
      setPanelHeight(PANEL_NAME, collapsed ? 40 : rect.height);
    });
  }, [collapsed]);

  // RESTORE — yalnız PAFTA değişince: konum + açık/kapalı geri yükle. getStackTop'a
  // bağlı DEĞİL → açma/kapama bu effect'i tetiklemez.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stored = panels[PANEL_NAME];
    if (stored?.dragged) {
      el.style.left = `${stored.left}px`;
      el.style.top = `${stored.top}px`;
      wasDragged.current = true;
    } else {
      wasDragged.current = false;
      const stackTop = getStackTop(PANEL_NAME);
      el.style.left = '';
      el.style.top = `${stackTop}px`;
      el.style.maxHeight = `${window.innerHeight - stackTop}px`;
    }
    const wantCollapsed = stored && typeof stored.collapsed === 'boolean' ? stored.collapsed : true;
    if (wantCollapsed !== collapsed) setCollapsed(wantCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Pafta-İÇİ yeniden stack (sürüklenmemiş paneller)
  useEffect(() => {
    const el = ref.current;
    if (!el || wasDragged.current) return;
    const stackTop = getStackTop(PANEL_NAME);
    el.style.top = `${stackTop}px`;
    el.style.maxHeight = `${window.innerHeight - stackTop}px`;
  }, [getStackTop]);

  useEffect(() => {
    const el = ref.current;
    const header = headerRef.current;

    const onMouseDown = (e) => {
      if (isResizing.current) return;
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - el.offsetLeft,
        y: e.clientY - el.offsetTop,
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      let x = e.clientX - dragOffset.current.x;
      let y = e.clientY - dragOffset.current.y;
      const ww = window.innerWidth;
      const w = el.offsetWidth;

      if (x < SNAP_THRESHOLD) { x = 0; showSnapHighlight(); }
      if (x + w > ww - SNAP_THRESHOLD) { x = ww - w; showSnapHighlight(); }
      if (y < TOPBAR_HEIGHT + SNAP_THRESHOLD) { y = TOPBAR_HEIGHT; showSnapHighlight(); }

      const h = el.offsetHeight;
      Object.entries(panels).forEach(([key, p]) => {
        if (key === PANEL_NAME) return;
        if (Math.abs(x - (p.left + p.width)) < SNAP_THRESHOLD && Math.abs(y - p.top) < p.height) { x = p.left + p.width; y = p.top; showSnapHighlight(); }
        if (Math.abs(x + w - p.left) < SNAP_THRESHOLD && Math.abs(y - p.top) < p.height) { x = p.left - w; y = p.top; showSnapHighlight(); }
        if (Math.abs(y - (p.top + p.height)) < SNAP_THRESHOLD && Math.abs(x - p.left) < p.width) { y = p.top + p.height; x = p.left; showSnapHighlight(); }
        if (Math.abs(y + h - p.top) < SNAP_THRESHOLD && Math.abs(x - p.left) < p.width) { y = p.top - h; x = p.left; showSnapHighlight(); }
      });

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.maxHeight = `${window.innerHeight - y}px`;
      wasDragged.current = true;
      updatePanelPosition(PANEL_NAME, { top: y, left: x, dragged: true });
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    header.addEventListener('mousedown', onMouseDown);
    return () => header.removeEventListener('mousedown', onMouseDown);
  }, [panels]);

  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    const rect = ref.current.getBoundingClientRect();
    startSize.current = { width: rect.width, height: rect.height < panelHeight ? rect.height : panelHeight };
    document.addEventListener('mousemove', handleResizing);
    document.addEventListener('mouseup', stopResizing);
  };

  const handleResizing = (e) => {
    if (!isResizing.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    const newWidth = startSize.current.width + dx;
    const newHeight = startSize.current.height + dy;
    if (newWidth > 200) ref.current.style.width = `${newWidth}px`;
    if (newHeight < panelHeight) ref.current.style.height = `${newHeight}px`;
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResizing);
    document.removeEventListener('mouseup', stopResizing);
  };

  return (
    <div
      ref={ref}
      className={`KabloBacasiPanel ${collapsed ? 'collapsed' : ''}`}
      style={{ height: collapsed ? '40px' : undefined }}
    >
      <div className="KabloBacasiPanel-header" ref={headerRef}>
        <span onClick={toggle}>Kablo Bacasi</span>
        <span style={{ fontSize: '18px', cursor: 'pointer' }} onClick={toggle}>
          {collapsed ? '▼' : '▲'}
        </span>
      </div>
      <div className="KabloBacasiPanel-content">
        <table className="info-table-grid">
          <tbody>
            {FIELDS.map(field => {
              const val = getValue(field);
              const isAuto = isAutoField(field);
              return (
                <tr key={field.key}>
                  <td className="info-label">
                    {field.label}
                    {field.auto && (
                      <span style={{ fontSize: 9, color: isAuto ? '#4fc3f7' : '#ff9800', marginLeft: 4 }}>
                        {isAuto ? '(oto)' : '(manuel)'}
                      </span>
                    )}
                  </td>
                  <td className="info-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      className="info-input"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={val}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                    />
                    {field.auto && !isAuto && (
                      <button
                        style={{
                          background: 'none', border: '1px solid #555', color: '#4fc3f7',
                          cursor: 'pointer', fontSize: 10, padding: '1px 4px', borderRadius: 3,
                          whiteSpace: 'nowrap',
                        }}
                        title="Otomatik hesaplamaya don"
                        onClick={() => handleReset(field.key)}
                      >
                        oto
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="resize-handle" onMouseDown={handleResizeMouseDown} />
    </div>
  );
};

export default KabloBacasiPanel;
